import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useToast } from "@/lib/toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface PortfolioSummary {
  counts: { total: number; active: number; available_now: number };
  by_stay: { long_term: number; medium_term: number; short_term: number; unspecified: number };
  by_type: { villa: number; apartment: number; house: number; room: number; land: number };
  areas: string[];
}

export function BusinessPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: settings } = useQuery({
    queryKey: ["settings", "/api/settings/business"],
    queryFn: () => api.get<Record<string, unknown>>("/api/settings/business"),
  });
  const { data: portfolio } = useQuery({
    queryKey: ["business", "portfolio"],
    queryFn: () => api.get<PortfolioSummary>("/api/business/portfolio-summary"),
    refetchInterval: 60_000,
  });

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const saveMut = useMutation({
    mutationFn: () => api.put("/api/settings/business", draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "/api/settings/business"] });
      const n = Object.keys(draft).length;
      setDraft({});
      toast.success("Business identity saved", `${n} field${n > 1 ? "s" : ""} updated`);
    },
    onError: (e) => toast.error("Save failed", e instanceof Error ? e.message : String(e)),
  });

  const get = (k: string) => (draft[k] !== undefined ? draft[k] : settings?.[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });
  const dirty = Object.keys(draft).length > 0;

  function getCities(): string[] {
    const raw = get("cities_covered");
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch { /* noop */ }
    }
    return [];
  }
  function setCities(next: string[]) {
    set("cities_covered", next);
  }

  if (!settings) {
    return <Card><CardContent className="py-6 text-sm text-slate-500">Loading…</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Business</h1>
        <p className="text-sm text-slate-500">
          Define who you are, who you target, and where you operate. This context is injected into the AI's system prompt on every reply.
        </p>
      </header>

      <PortfolioCard portfolio={portfolio} />

      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
          <CardDescription>The big picture - what's the business about?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="Business name (used by the AI when talking to prospects)">
            <Input value={String(get("business_name") ?? "")} onChange={(e) => set("business_name", e.target.value)} />
          </Field>
          <Field label="Tagline / one-liner">
            <Input
              placeholder="ex: Long-term villa rentals in Bali for digital nomads"
              maxLength={280}
              value={String(get("tagline") ?? "")}
              onChange={(e) => set("tagline", e.target.value)}
            />
          </Field>
          <Field label="Long description (≤8000 chars)">
            <Textarea
              rows={4}
              placeholder="A few paragraphs about your business, what makes you different, your background, your value prop."
              value={String(get("description") ?? "")}
              onChange={(v) => set("description", v)}
            />
          </Field>
          <Field label="Description of what you offer (≤8000 chars - appears in the AI prompt as 'what we offer')">
            <Textarea
              rows={4}
              placeholder="ex: We rent fully-furnished 2-4 bedroom villas with private pools in Canggu and Berawa for stays of 6 months or more. Cleaning twice a week + Starlink internet included."
              value={String(get("offering_description") ?? "")}
              onChange={(v) => set("offering_description", v)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ideal Customer Profile (ICP)</CardTitle>
          <CardDescription>
            The SEGMENT you target - demographic & psychographic traits. The AI uses this to qualify, anticipate questions, and stay on-message.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="ICP - Profile (who they are)">
            <Textarea
              rows={3}
              placeholder="ex: Digital nomads & remote workers, 28-45, monthly income 4-10k EUR, currently traveling SE Asia, staying 3-12 months at a time."
              value={String(get("icp_description") ?? "")}
              onChange={(v) => set("icp_description", v)}
            />
          </Field>
          <Field label="Pain points they have">
            <Textarea
              rows={3}
              placeholder="ex: Hotels too expensive long-term, AirBnBs unstable for visa runs, want a 'home' not a 'place to crash', need reliable internet for work calls."
              value={String(get("icp_painpoints") ?? "")}
              onChange={(v) => set("icp_painpoints", v)}
            />
          </Field>
          <Field label="What they seek">
            <Textarea
              rows={3}
              placeholder="ex: Quiet area but close to cafés & gym, private pool, 100+ Mbps internet, fully furnished, clear lease terms."
              value={String(get("icp_what_they_seek") ?? "")}
              onChange={(v) => set("icp_what_they_seek", v)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ideal Customer Avatar (IAP)</CardTitle>
          <CardDescription>
            ONE fictional customer that embodies the ICP. The AI uses the persona to mirror tone, anticipate objections, and stay specific.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Persona name (fictional)">
              <Input
                placeholder="ex: Lucas, 32"
                value={String(get("iap_avatar_name") ?? "")}
                onChange={(e) => set("iap_avatar_name", e.target.value)}
              />
            </Field>
            <Field label="Story (3 lines)">
              <Textarea
                rows={3}
                placeholder="ex: Lucas is a French freelance designer, did 6 months in Lisbon and now wants to spend Sept-March in Bali to escape European winter."
                value={String(get("iap_avatar_story") ?? "")}
                onChange={(v) => set("iap_avatar_story", v)}
              />
            </Field>
          </div>
          <Field label="Vocabulary they use">
            <Textarea
              rows={2}
              placeholder="ex: 'co-living', 'long-term stay', 'workspace', 'WiFi speed', 'rooftop', 'scooter access'"
              value={String(get("iap_avatar_vocabulary") ?? "")}
              onChange={(v) => set("iap_avatar_vocabulary", v)}
            />
          </Field>
          <Field label="Typical objections">
            <Textarea
              rows={3}
              placeholder="ex: 'Can I cancel if my plans change?', 'Is the internet really 100 Mbps?', 'Can I see another villa first?'"
              value={String(get("iap_avatar_objections") ?? "")}
              onChange={(v) => set("iap_avatar_objections", v)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Geographic Coverage</CardTitle>
          <CardDescription>
            Cities or areas you operate in. The AI will refuse / escalate when a prospect asks about places not listed here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="Cities / areas covered">
            <CityChips cities={getCities()} onChange={setCities} />
          </Field>
          <Field label="Cities / areas we do NOT cover (helps the AI politely escalate)">
            <Textarea
              rows={2}
              placeholder="ex: Sanur, Lovina, Lombok"
              value={String(get("areas_not_covered") ?? "")}
              onChange={(v) => set("areas_not_covered", v)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Audience description (extra free-form)</CardTitle>
          <CardDescription>Anything else worth saying about who you talk to that didn't fit above.</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={3}
            placeholder="Optional - leave blank if the ICP / persona sections are enough."
            value={String(get("audience_description") ?? "")}
            onChange={(v) => set("audience_description", v)}
          />
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <div className="rounded-md bg-brand-surface shadow-md border border-slate-200 px-3 py-2 flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {dirty ? `${Object.keys(draft).length} unsaved change(s)` : "All saved"}
          </span>
          <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio summary (auto-computed, read-only)
// ---------------------------------------------------------------------------

function PortfolioCard({ portfolio }: { portfolio: PortfolioSummary | undefined }) {
  if (!portfolio) {
    return (
      <Card>
        <CardContent className="py-4 text-xs text-slate-500">Loading portfolio summary…</CardContent>
      </Card>
    );
  }
  const { counts, by_stay, by_type, areas } = portfolio;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your portfolio (auto-computed)</CardTitle>
        <CardDescription>
          Computed live from <strong>Properties</strong>. To change these numbers, edit individual properties - not here.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Total active" value={counts.active} />
          <Stat label="Available now" value={counts.available_now} accent />
          <Stat label="All-time" value={counts.total} muted />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <BadgeCount label="Long-term (≥6m)" value={by_stay.long_term} />
          <BadgeCount label="Medium (1-5m)" value={by_stay.medium_term} />
          <BadgeCount label="Short-term (<1m)" value={by_stay.short_term} />
          <BadgeCount label="No min_stay set" value={by_stay.unspecified} />
        </div>
        <div className="flex flex-wrap gap-1.5 text-xs">
          {Object.entries(by_type).filter(([, n]) => n > 0).map(([t, n]) => (
            <Badge key={t} variant="outline">{t} × {n}</Badge>
          ))}
        </div>
        {areas.length > 0 && (
          <div className="text-xs text-slate-600">
            <span className="text-slate-500">Areas in your catalog:</span>{" "}
            {areas.join(", ")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, accent, muted }: { label: string; value: number; accent?: boolean; muted?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${accent ? "bg-green-50 border-green-200" : muted ? "bg-slate-50 border-slate-200" : "bg-brand-surface border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${accent ? "text-green-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function BadgeCount({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1 flex items-center justify-between gap-2">
      <span className="text-slate-600">{label}</span>
      <span className="font-mono text-slate-900">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small atoms
// ---------------------------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function Textarea({ rows, placeholder, value, onChange }: { rows: number; placeholder?: string; value: string; onChange: (v: string) => void }) {
  return (
    <textarea
      rows={rows}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
    />
  );
}

function CityChips({ cities, onChange }: { cities: string[]; onChange: (next: string[]) => void }) {
  const [input, setInput] = useState("");
  function add() {
    const v = input.trim();
    if (!v) return;
    if (cities.includes(v)) { setInput(""); return; }
    onChange([...cities, v]);
    setInput("");
  }
  function remove(c: string) {
    onChange(cities.filter((x) => x !== c));
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {cities.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs">
            {c}
            <button type="button" onClick={() => remove(c)} className="text-slate-400 hover:text-slate-700">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {cities.length === 0 && (
          <span className="text-xs text-slate-400">No cities yet - add some below.</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Type a city and press Enter (ex: Canggu)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>Add</Button>
      </div>
    </div>
  );
}
